import React, { useRef, useState } from 'react';
import { EntityCard, EntityCardSize, Stack, Box } from '@/components';
import { ReportSearchModel, ReportCardType, UiConfigurationModel } from '@/types';
import Flicking, { ChangedEvent, NeedPanelEvent } from '@egjs/react-flicking';
import { DIRECTION } from '@egjs/flicking';
import { carouselMotion, carouselItemMotion } from '@/animations';
import ReportCarouselHeading, { ReportHeadingModel } from './ReportCarouselHeading';

interface ReportTwoRowsDragCarouselModel {
  cards: ReportSearchModel[];
  cardType: ReportCardType;
  heading?: ReportHeadingModel;
  uiConfiguration?: UiConfigurationModel;
  onNeedPanel(event: NeedPanelEvent): void;
  onCardClick?: (e: React.MouseEvent, data: ReportSearchModel) => void;
}

const ReportTwoRowsDragCarousel = (data: ReportTwoRowsDragCarouselModel) => {
  const { cards, cardType, onNeedPanel, onCardClick } = data;
  const [initialAnimationComplete, setInitialAnimationComplete] = useState<boolean>(false);
  const [nextDisabled, setNextDisabled] = useState<boolean>(false);
  const [prevDisabled, setPrevDisabled] = useState<boolean>(true);

  const carousel = useRef<Flicking>(null);

  const onCarouselChanged = ({ currentTarget, direction }: ChangedEvent) => {
    if (!currentTarget.camera.atEdge) {
      setPrevDisabled(false);
      setNextDisabled(false);
    } else {
      if (direction === DIRECTION.NEXT) {
        setPrevDisabled(false);
        setNextDisabled(true);
      } else {
        setPrevDisabled(true);
        setNextDisabled(false);
      }
    }
  };

  const splitCards: { a?: ReportSearchModel; b?: ReportSearchModel }[] = [];
  cards.forEach((item, index) => {
    if (index % 2 === 0) {
      splitCards.push({ a: item });
    } else {
      splitCards[splitCards.length - 1].b = item;
    }
  });

  const onPrevClick = () => {
    if (carousel.current && !carousel.current.animating) {
      carousel.current.prev();
    }
  };
  const onNextClick = () => {
    if (carousel.current && !carousel.current.animating) {
      carousel.current.next();
    }
  };

  return (
    <Stack
      animation={initialAnimationComplete ? { ...carouselMotion, animate: 'active' } : carouselMotion}
      onAnimationComplete={() => setInitialAnimationComplete(true)}
    >
      <div className="container">
        <ReportCarouselHeading
          heading={data.heading}
          nextDisabled={nextDisabled}
          prevDisabled={prevDisabled}
          onNextClick={onNextClick}
          onPrevClick={onPrevClick}
          hideArrows={data.uiConfiguration?.hideArrows}
          hideViewMore={data.uiConfiguration?.hideViewMore}
        />
      </div>

      <Flicking
        ref={carousel}
        preventClickOnDrag
        preventDefaultOnDrag
        align="prev"
        horizontal
        bound
        onNeedPanel={onNeedPanel}
        className="xxl:pl-[242px] !overflow-visible pl-3 sm:pl-4 xl:pl-[135px]"
        onChanged={onCarouselChanged}
      >
        {splitCards.map((card, index) => (
          <div key={index} className="mx-1 flex sm:mx-[11px] sm:block">
            {card.a && (
              <Box animation={carouselItemMotion}>
                <EntityCard data={card.a} cardType={cardType} size={EntityCardSize.SMALL} onClick={onCardClick} />
              </Box>
            )}
            {card.b && (
              <Box animation={carouselItemMotion} className="mt-0 ml-2 sm:mt-2 sm:ml-0 md:mt-6">
                <EntityCard data={card.b} cardType={cardType} size={EntityCardSize.SMALL} onClick={onCardClick} />
              </Box>
            )}
          </div>
        ))}
      </Flicking>
    </Stack>
  );
};

export default ReportTwoRowsDragCarousel;
